import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import Select from 'react-select'
import { useThemeMode } from '../../../../../../../_metronic/partials'
import { SelectStyles } from '../../../../../../../components/Select'
import Swal from 'sweetalert2'
import { createCredentialRule, updateCredentialRule, updateCredentialRuleKeys } from '../../core/api'
import { IProviderCredentialRuleCreateBody, IProviderCredentialRuleUpdateBody, IProviderCredentialRuleUpdateKeysBody } from '../../core/requests.types'
import { IProviderCredentialRule, IProviderFormMeta, TrafficType } from '../../core/types'
import { PROVIDER_DIRECTIONS, TRAFFIC_TYPES } from '../../core/consts'
import { Currency } from '../../../../core/_models'
import { getGeneralError } from '../../../../../../common/api/errors'

interface Props {
  show: boolean
  providerId: number
  formMeta: IProviderFormMeta | null
  rule?: IProviderCredentialRule | null
  existingRules: IProviderCredentialRule[]
  canUpdateKeys?: boolean
  onClose: (open: boolean) => void
  onSaved: (rule: IProviderCredentialRule) => void
}

type StringOption = { value: string; label: string }

const CURRENCY_OPTIONS: StringOption[] = (Object.values(Currency) as string[]).map((c) => ({ value: c, label: c }))

export default ({ show, providerId, formMeta, rule, existingRules, canUpdateKeys = false, onClose, onSaved }: Props) => {
  const { mode: theme } = useThemeMode()
  const [merchantIds, setMerchantIds] = useState<number[]>([])
  const [directions, setDirections] = useState<Array<'PAYMENT' | 'PAYOUT'>>([])
  const [trafficTypes, setTrafficTypes] = useState<TrafficType[]>([])
  const [paymentMethodIds, setPaymentMethodIds] = useState<number[]>([])
  const [currencies, setCurrencies] = useState<string[]>([])
  const [minAmount, setMinAmount] = useState<string>('')
  const [maxAmount, setMaxAmount] = useState<string>('')
  const [externalMerchantId, setExternalMerchantId] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [publicKey, setPublicKey] = useState('')
  const [privateKey, setPrivateKey] = useState('')
  const [privateKeyPassphrase, setPrivateKeyPassphrase] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isUpdatingKeys, setIsUpdatingKeys] = useState(false)
  const [showKeyUpdateForm, setShowKeyUpdateForm] = useState(false)
  const [updateApiKey, setUpdateApiKey] = useState('')
  const [updatePublicKey, setUpdatePublicKey] = useState('')
  const [updatePrivateKey, setUpdatePrivateKey] = useState('')
  const [updatePrivateKeyPassphrase, setUpdatePrivateKeyPassphrase] = useState('')

  const isEdit = !!rule

  useEffect(() => {
    if (!show) return

    setMerchantIds(rule?.merchants?.map((m) => m.id) ?? [])
    setDirections(rule?.directions ?? [])
    setTrafficTypes(rule?.trafficTypes ?? [])
    setPaymentMethodIds(rule?.paymentMethods?.map((m) => m.id) ?? [])
    setCurrencies(rule?.currencies ?? [])
    setMinAmount(rule?.minAmount !== null && rule?.minAmount !== undefined ? String(rule.minAmount) : '')
    setMaxAmount(rule?.maxAmount !== null && rule?.maxAmount !== undefined ? String(rule.maxAmount) : '')
    setExternalMerchantId('')
    setApiKey('')
    setPublicKey('')
    setPrivateKey('')
    setPrivateKeyPassphrase('')
    setShowKeyUpdateForm(false)
    setUpdateApiKey('')
    setUpdatePublicKey('')
    setUpdatePrivateKey('')
    setUpdatePrivateKeyPassphrase('')
  }, [show, rule])

  // Build set of occupied (merchantId:direction:paymentMethodId) tuples from OTHER rules
  const occupiedTuples = useMemo(() => {
    const set = new Set<string>()
    existingRules
      .filter((r) => r.id !== rule?.id)
      .forEach((r) => {
        (r.merchants ?? []).forEach((m) => {
          (r.directions ?? []).forEach((d) => {
            (r.paymentMethods ?? []).forEach((pm) => {
              (r.trafficTypes ?? []).forEach((tt) => {
                set.add(`${m.id}:${d}:${pm.id}:${tt}`)
              })
            })
          })
        })
      })
    return set
  }, [existingRules, rule])

  // An option is disabled if adding it would make ALL combos with current selection occupied
  const isMerchantDisabled = useCallback(
    (merchantId: number) => {
      if (!directions.length || !paymentMethodIds.length || !trafficTypes.length) return false
      return directions.every((d) => paymentMethodIds.every((pm) => trafficTypes.every((tt) => occupiedTuples.has(`${merchantId}:${d}:${pm}:${tt}`))))
    },
    [directions, paymentMethodIds, trafficTypes, occupiedTuples],
  )

  // Selectable merchant values = all lookup merchants that are not disabled by tuple conflicts
  const selectableMerchantValues = useMemo(
    () => (formMeta?.merchants ?? []).filter((opt) => !isMerchantDisabled(opt.value)).map((opt) => opt.value),
    [formMeta, isMerchantDisabled],
  )

  // "Add all" is active while at least one selectable merchant is not yet selected
  const canAddAllMerchants = useMemo(
    () => selectableMerchantValues.some((v) => !merchantIds.includes(v)),
    [selectableMerchantValues, merchantIds],
  )

  const canClearMerchants = merchantIds.length > 0

  const handleAddAllMerchants = useCallback(() => {
    // Append selectable merchants without duplicating the current selection
    setMerchantIds((prev) => Array.from(new Set([...prev, ...selectableMerchantValues])))
  }, [selectableMerchantValues])

  const handleClearMerchants = useCallback(() => {
    setMerchantIds([])
  }, [])

  const isDirectionDisabled = useCallback(
    (direction: string) => {
      if (!merchantIds.length || !paymentMethodIds.length || !trafficTypes.length) return false
      return merchantIds.every((m) => paymentMethodIds.every((pm) => trafficTypes.every((tt) => occupiedTuples.has(`${m}:${direction}:${pm}:${tt}`))))
    },
    [merchantIds, paymentMethodIds, trafficTypes, occupiedTuples],
  )

  const isPaymentMethodDisabled = useCallback(
    (pmId: number) => {
      if (!merchantIds.length || !directions.length || !trafficTypes.length) return false
      return merchantIds.every((m) => directions.every((d) => trafficTypes.every((tt) => occupiedTuples.has(`${m}:${d}:${pmId}:${tt}`))))
    },
    [merchantIds, directions, trafficTypes, occupiedTuples],
  )

  const isTrafficTypeDisabled = useCallback(
    (tt: string) => {
      if (!merchantIds.length || !directions.length || !paymentMethodIds.length) return false
      return merchantIds.every((m) => directions.every((d) => paymentMethodIds.every((pm) => occupiedTuples.has(`${m}:${d}:${pm}:${tt}`))))
    },
    [merchantIds, directions, paymentMethodIds, occupiedTuples],
  )

  const hasConflict = useMemo(() => {
    if (!merchantIds.length || !directions.length || !paymentMethodIds.length || !trafficTypes.length) return false
    return merchantIds.some((m) =>
      directions.some((d) => paymentMethodIds.some((pm) => trafficTypes.some((tt) => occupiedTuples.has(`${m}:${d}:${pm}:${tt}`)))),
    )
  }, [merchantIds, directions, paymentMethodIds, trafficTypes, occupiedTuples])

  const isFormComplete = useMemo(() => {
    if (!merchantIds.length || !directions.length || !paymentMethodIds.length || !trafficTypes.length) return false
    if (hasConflict) return false
    if (isEdit) return true
    return [apiKey, publicKey, privateKey, privateKeyPassphrase].some((v) => v.trim().length > 0)
  }, [merchantIds, directions, paymentMethodIds, trafficTypes, hasConflict, isEdit, apiKey, publicKey, privateKey, privateKeyPassphrase])

  const isKeyUpdateFormComplete = useMemo(
    () => [updateApiKey, updatePublicKey, updatePrivateKey, updatePrivateKeyPassphrase].some((v) => v.trim().length > 0),
    [updateApiKey, updatePublicKey, updatePrivateKey, updatePrivateKeyPassphrase],
  )

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      const parsedMin = minAmount.trim() ? Number(minAmount) : null
      const parsedMax = maxAmount.trim() ? Number(maxAmount) : null

      const response = isEdit
        ? await updateCredentialRule(providerId, rule!.id, {
            merchantIds,
            directions,
            trafficTypes,
            paymentMethodIds,
            currencies: currencies.length > 0 ? currencies : null,
            minAmount: parsedMin,
            maxAmount: parsedMax,
          } as IProviderCredentialRuleUpdateBody)
        : await createCredentialRule(providerId, {
            merchantIds,
            directions,
            trafficTypes,
            paymentMethodIds,
            externalMerchantId: externalMerchantId.trim() || null,
            keys: {
              apiKey,
              publicKey,
              privateKey,
              privateKeyPassphrase,
            },
          } as IProviderCredentialRuleCreateBody)

      if (!response?.data?.success) {
        throw new Error((response?.data as any)?.errors?.general_error || 'Unknown error')
      }

      onSaved(response.data.data)
      onClose(false)
    } catch (error: any) {
      Swal.fire({ title: 'Ошибка', text: getGeneralError(error) || error?.message || 'Не удалось сохранить credential-связку', icon: 'error' })
    } finally {
      setIsSaving(false)
    }
  }, [
    isEdit, providerId, rule, merchantIds, directions, trafficTypes, paymentMethodIds,
    currencies, minAmount, maxAmount, externalMerchantId,
    apiKey, publicKey, privateKey, privateKeyPassphrase, onSaved, onClose,
  ])

  const handleUpdateKeys = useCallback(async () => {
    if (!rule) return
    setIsUpdatingKeys(true)
    try {
      const body: IProviderCredentialRuleUpdateKeysBody = {}
      if (updateApiKey.trim()) body.apiKey = updateApiKey.trim()
      if (updatePublicKey.trim()) body.publicKey = updatePublicKey.trim()
      if (updatePrivateKey.trim()) body.privateKey = updatePrivateKey.trim()
      if (updatePrivateKeyPassphrase.trim()) body.privateKeyPassphrase = updatePrivateKeyPassphrase.trim()

      const response = await updateCredentialRuleKeys(providerId, rule.id, body)
      if (!response?.data?.success) {
        throw new Error((response?.data as any)?.errors?.general_error || 'Unknown error')
      }

      onSaved(response.data.data)
      setShowKeyUpdateForm(false)
      setUpdateApiKey('')
      setUpdatePublicKey('')
      setUpdatePrivateKey('')
      setUpdatePrivateKeyPassphrase('')
    } catch (error: any) {
      Swal.fire({ title: 'Ошибка', text: getGeneralError(error) || error?.message || 'Не удалось обновить ключи', icon: 'error' })
    } finally {
      setIsUpdatingKeys(false)
    }
  }, [rule, providerId, updateApiKey, updatePublicKey, updatePrivateKey, updatePrivateKeyPassphrase, onSaved])

  return (
    <Modal show={show} onHide={() => !isSaving && !isUpdatingKeys && onClose(false)} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? 'Редактирование credential-связки' : 'Новая credential-связка'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <style>{`
          .merchant-bulk-link {
            background: none;
            border: none;
            padding: 0;
            font-size: 0.85rem;
            font-weight: 500;
            line-height: 1;
            color: var(--bs-gray-500);
            cursor: pointer;
            transition: color .15s ease, transform .06s ease, opacity .15s ease;
          }
          .merchant-bulk-link:hover:not(:disabled) { color: var(--bs-primary); }
          .merchant-bulk-link:active:not(:disabled) { transform: translateY(1px); }
          .merchant-bulk-link:disabled { color: var(--bs-gray-400); opacity: .55; cursor: default; }
        `}</style>
        <div className='fv-row mb-7'>
          <label className='required fs-6 fw-semibold mb-2'>Мерчанты</label>
          <Select
            styles={SelectStyles(theme)}
            isMulti
            options={(formMeta?.merchants ?? []).map((opt) => ({
              ...opt,
              isDisabled: isMerchantDisabled(opt.value),
            }))}
            value={(formMeta?.merchants ?? []).filter((item) => merchantIds.includes(item.value))}
            onChange={(items) => setMerchantIds((items || []).map((item) => item.value))}
            isDisabled={isSaving}
          />
          <div className='d-flex align-items-center gap-3 mt-2'>
            <button
              type='button'
              className='merchant-bulk-link'
              onClick={handleAddAllMerchants}
              disabled={isSaving || !canAddAllMerchants}
            >
              Добавить всех мерчантов
            </button>
            <span className='text-muted'>·</span>
            <button
              type='button'
              className='merchant-bulk-link'
              onClick={handleClearMerchants}
              disabled={isSaving || !canClearMerchants}
            >
              Снять всех
            </button>
          </div>
        </div>

        <div className='fv-row mb-7'>
          <label className='required fs-6 fw-semibold mb-2'>Направления</label>
          <Select
            styles={SelectStyles(theme)}
            isMulti
            options={PROVIDER_DIRECTIONS.map((opt) => ({
              ...opt,
              isDisabled: isDirectionDisabled(opt.value),
            }))}
            value={PROVIDER_DIRECTIONS.filter((item) => directions.includes(item.value))}
            onChange={(items) => setDirections((items || []).map((item) => item.value))}
            isDisabled={isSaving}
          />
        </div>

        <div className='fv-row mb-7'>
          <label className='required fs-6 fw-semibold mb-2'>Тип трафика</label>
          <Select
            styles={SelectStyles(theme)}
            isMulti
            options={TRAFFIC_TYPES.map((opt) => ({
              ...opt,
              isDisabled: isTrafficTypeDisabled(opt.value),
            }))}
            value={TRAFFIC_TYPES.filter((item) => trafficTypes.includes(item.value))}
            onChange={(items) => setTrafficTypes((items || []).map((item) => item.value))}
            isDisabled={isSaving}
          />
        </div>

        <div className='fv-row mb-7'>
          <label className='required fs-6 fw-semibold mb-2'>Платежные методы</label>
          <Select
            styles={SelectStyles(theme)}
            isMulti
            options={(formMeta?.paymentMethods ?? []).map((opt) => ({
              ...opt,
              isDisabled: isPaymentMethodDisabled(opt.value),
            }))}
            value={(formMeta?.paymentMethods ?? []).filter((item) => paymentMethodIds.includes(item.value))}
            onChange={(items) => setPaymentMethodIds((items || []).map((item) => item.value))}
            isDisabled={isSaving}
          />
        </div>

        {hasConflict && (
          <div className='alert alert-danger d-flex align-items-center mb-7 p-5'>
            <div className='fs-6'>
              Выбранная комбинация мерчант / направление / тип трафика / платежный метод уже используется в другой связке этого провайдера.
            </div>
          </div>
        )}

        {isEdit && (
          <>
            <div className='fv-row mb-7'>
              <label className='fs-6 fw-semibold mb-2'>Валюты</label>
              <Select
                styles={SelectStyles(theme)}
                isMulti
                options={CURRENCY_OPTIONS}
                value={CURRENCY_OPTIONS.filter((opt) => currencies.includes(opt.value))}
                onChange={(items) => setCurrencies((items || []).map((item) => item.value))}
                placeholder='Любая (не ограничено)'
                isDisabled={isSaving}
              />
              <div className='form-text text-muted'>Если не выбрано — принимаются любые валюты.</div>
            </div>

            <div className='row mb-7'>
              <div className='col-md-6'>
                <label className='fs-6 fw-semibold mb-2'>Мин. сумма операции</label>
                <input
                  className='form-control form-control-solid'
                  type='number'
                  min='0'
                  placeholder='Без ограничения'
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className='col-md-6'>
                <label className='fs-6 fw-semibold mb-2'>Макс. сумма операции</label>
                <input
                  className='form-control form-control-solid'
                  type='number'
                  min='0'
                  placeholder='Без ограничения'
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className='col-12 mt-2'>
                <div className='form-text text-muted'>
                  Если поле не заполнено — будут проходить операции на любую сумму.
                </div>
              </div>
            </div>

            <div className='fv-row mb-7'>
              <label className='fs-6 fw-semibold mb-2'>Внешний ID мерчанта у провайдера</label>
              <input
                className='form-control form-control-solid bg-light'
                value={rule?.externalMerchantId ?? '—'}
                readOnly
              />
              <div className='form-text text-muted'>Устанавливается при создании связки, изменить нельзя.</div>
            </div>

            <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-6'>
              <div className='d-flex flex-stack flex-grow-1'>
                <div className='fw-semibold flex-grow-1'>
                  <div className='d-flex justify-content-between align-items-start'>
                    <div className='fs-6 text-gray-700'>
                      Значения ключей скрыты. При редактировании можно менять только связку merchant / направление / платежный метод и параметры суммы/валюты.
                    </div>
                    {canUpdateKeys && !showKeyUpdateForm && (
                      <button
                        type='button'
                        className='btn btn-link btn-sm text-primary p-0 ms-3 text-nowrap'
                        onClick={() => setShowKeyUpdateForm(true)}
                        disabled={isSaving}
                      >
                        Обновить ключи
                      </button>
                    )}
                  </div>
                  <div className='text-muted mt-3'>
                    API key: {rule?.keysState.apiKey ? 'Установлен' : 'Не установлен'}<br />
                    Public key: {rule?.keysState.publicKey ? 'Установлен' : 'Не установлен'}<br />
                    Private key: {rule?.keysState.privateKey ? 'Установлен' : 'Не установлен'}<br />
                    Passphrase: {rule?.keysState.privateKeyPassphrase ? 'Установлен' : 'Не установлен'}
                  </div>
                </div>
              </div>
            </div>

            {showKeyUpdateForm && (
              <div className='mt-6'>
                <div className='alert alert-warning d-flex align-items-center mb-6 p-5'>
                  <div className='fs-7'>
                    Внимание: новые ключи применятся для всех связок провайдера, которые используют эти ключи.
                  </div>
                </div>
                {rule?.keysState.apiKey && (
                  <div className='fv-row mb-5'>
                    <label className='fs-6 fw-semibold mb-2'>API key</label>
                    <textarea
                      className='form-control form-control-solid font-monospace'
                      rows={3}
                      style={{ resize: 'none' }}
                      value={updateApiKey}
                      onChange={(e) => setUpdateApiKey(e.target.value)}
                      disabled={isUpdatingKeys}
                    />
                  </div>
                )}
                {rule?.keysState.publicKey && (
                  <div className='fv-row mb-5'>
                    <label className='fs-6 fw-semibold mb-2'>Public key</label>
                    <textarea
                      className='form-control form-control-solid font-monospace'
                      rows={3}
                      style={{ resize: 'none' }}
                      value={updatePublicKey}
                      onChange={(e) => setUpdatePublicKey(e.target.value)}
                      disabled={isUpdatingKeys}
                    />
                  </div>
                )}
                {rule?.keysState.privateKey && (
                  <div className='fv-row mb-5'>
                    <label className='fs-6 fw-semibold mb-2'>Private key</label>
                    <textarea
                      className='form-control form-control-solid font-monospace'
                      rows={3}
                      style={{ resize: 'none' }}
                      value={updatePrivateKey}
                      onChange={(e) => setUpdatePrivateKey(e.target.value)}
                      disabled={isUpdatingKeys}
                    />
                  </div>
                )}
                {rule?.keysState.privateKeyPassphrase && (
                  <div className='fv-row mb-5'>
                    <label className='fs-6 fw-semibold mb-2'>Private key passphrase</label>
                    <textarea
                      className='form-control form-control-solid font-monospace'
                      rows={3}
                      style={{ resize: 'none' }}
                      value={updatePrivateKeyPassphrase}
                      onChange={(e) => setUpdatePrivateKeyPassphrase(e.target.value)}
                      disabled={isUpdatingKeys}
                    />
                  </div>
                )}
                <div className='form-text text-muted mb-5'>Заполните только те ключи, которые нужно обновить.</div>
                <div className='d-flex gap-3'>
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={handleUpdateKeys}
                    disabled={isUpdatingKeys || !isKeyUpdateFormComplete}
                  >
                    {isUpdatingKeys ? 'Сохранение...' : 'Сохранить ключи'}
                  </Button>
                  <Button
                    variant='secondary'
                    size='sm'
                    onClick={() => setShowKeyUpdateForm(false)}
                    disabled={isUpdatingKeys}
                  >
                    Отмена
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {!isEdit && (
          <>
            <div className='separator my-6' />
            <div className='fv-row mb-7'>
              <label className='fs-6 fw-semibold mb-2'>Внешний ID мерчанта у провайдера</label>
              <input
                className='form-control form-control-solid'
                placeholder='Например: ext-merchant-123'
                value={externalMerchantId}
                onChange={(e) => setExternalMerchantId(e.target.value)}
                disabled={isSaving}
              />
              <div className='form-text text-muted'>Необязательный идентификатор мерчанта во внешней системе провайдера.</div>
            </div>
            <div className='fv-row mb-7'>
              <label className='fs-6 fw-semibold mb-2'>API key</label>
              <textarea
                className='form-control form-control-solid font-monospace'
                rows={3}
                style={{ resize: 'none' }}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className='fv-row mb-7'>
              <label className='fs-6 fw-semibold mb-2'>Public key</label>
              <textarea
                className='form-control form-control-solid font-monospace'
                rows={3}
                style={{ resize: 'none' }}
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className='fv-row mb-7'>
              <label className='fs-6 fw-semibold mb-2'>Private key</label>
              <textarea
                className='form-control form-control-solid font-monospace'
                rows={3}
                style={{ resize: 'none' }}
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className='fv-row mb-0'>
              <label className='fs-6 fw-semibold mb-2'>Private key passphrase</label>
              <textarea
                className='form-control form-control-solid font-monospace'
                rows={3}
                style={{ resize: 'none' }}
                value={privateKeyPassphrase}
                onChange={(e) => setPrivateKeyPassphrase(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className='form-text text-muted mt-2'>Хотя бы один ключ должен быть заполнен.</div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={() => onClose(false)} disabled={isSaving || isUpdatingKeys}>
          Отменить
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={isSaving || isUpdatingKeys || !isFormComplete}>
          {isSaving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
